/**
 * Cover layout dispatcher — SLK-H Prompt 5.
 *
 * CANONICAL PATH (template-consistency): every PRODUCTION caller resolves the
 * cover through the single contract — `resolveReportPresentation(report,
 * template, theme).coverLayout` — and passes the result here as the explicit
 * `layout` prop, alongside the contract's EFFECTIVE `theme` (preset → palette +
 * typography). Each variant below re-skins from `props.theme.*`, so the cover
 * tracks the saved template/preset exactly like the body (#12 fix). The
 * on-screen viewer (SiteRagReportView), the Studio preview (which mounts that
 * viewer), the DOCX exporter, and the print/PDF path all funnel through that one
 * resolution — no renderer re-derives the cover variant or the theme.
 *
 * The legacy `resolveCoverLayout` fallback below is RETAINED only for the
 * cover-component unit tests that mount a single variant without a pre-resolved
 * presentation; it delegates to the same `src/lib/resolveCoverLayout.ts`
 * (template-first, then `theme.coverLayout`) the contract uses, so even the
 * fallback agrees with the contract. New call sites MUST pass `layout`.
 */
import React from 'react';
import { ClassicCover } from './ClassicCover';
import { MinimalCover } from './MinimalCover';
import { DataDenseCover } from './DataDenseCover';
import { HeroImageCover } from './HeroImageCover';
import { BoundaryPlateCover } from './BoundaryPlateCover';
import { DataSnapshotCover } from './DataSnapshotCover';
import { MastheadCover } from './MastheadCover';
import { DarkInstrumentCover } from './DarkInstrumentCover';
import { EditorialMastheadCover } from './EditorialMastheadCover';
import { ProspectusCover } from './ProspectusCover';
import { FrontispieceCover } from './FrontispieceCover';
import { TitleBlockCover } from './TitleBlockCover';
import { MemoCover } from './MemoCover';
import type { CoverProps } from './types';
import type { SiteRagReport } from '../../../../types/siteRag';
import type { ReportTheme } from '../../../../types/reportTheme';
import type { ReportTemplate } from '../../../../types/reportTemplate';
import { resolveCoverLayout as resolveCoverLayoutFromLib } from '../../../../lib/resolveCoverLayout';

/** @deprecated Prefer `resolveReportPresentation().coverLayout` or pass `layout` to `renderCover`. */
export function resolveCoverLayout(
  report: SiteRagReport,
  theme: ReportTheme,
  template?: ReportTemplate | null,
): NonNullable<ReportTheme['coverLayout']> {
  return resolveCoverLayoutFromLib(
    report,
    template ?? null,
    null,
    theme,
    !report.templateId || report.templateId === template?.id,
  );
}

/**
 * Render the chosen cover variant. Caller supplies the same prop set
 * every variant expects (CoverProps).
 */
export function renderCover(props: CoverProps & {
  layout?: NonNullable<ReportTheme['coverLayout']>;
  template?: ReportTemplate | null;
}): React.ReactElement {
  const layout = props.layout ?? resolveCoverLayout(props.report, props.theme, props.template ?? null);
  const coverProps: CoverProps = {
    ...props,
    heroCover: props.heroCover ?? props.template?.heroCover ?? null,
  };
  switch (layout) {
    case 'hero-image':     return <HeroImageCover {...coverProps} />;
    case 'data-dense':     return <DataDenseCover {...coverProps} />;
    case 'minimal':        return <MinimalCover {...coverProps} />;
    // ── ADDITIVE journal-aesthetic title pages ──
    case 'boundary-plate': return <BoundaryPlateCover {...coverProps} />;
    case 'data-snapshot':  return <DataSnapshotCover {...coverProps} />;
    case 'masthead':       return <MastheadCover {...coverProps} />;
    // ── K5 ADDITIVE opt-in dark instrument-panel title page ──
    case 'dark-instrument': return <DarkInstrumentCover {...coverProps} />;
    // ── Part L ADDITIVE flag-gated Editorial-Feature Monograph title page.
    //    Reaches here only when the resolved coverLayout is 'editorial-masthead'
    //    — i.e. the `editorial` preset under VITE_EDITORIAL_LAYOUTS. With the
    //    flag OFF nothing resolves to this value, so it is byte-inert. ──
    case 'editorial-masthead': return <EditorialMastheadCover {...coverProps} />;
    // ── Report Themes v2 ADDITIVE flag-gated title pages. Each reaches here only
    //    when the resolved coverLayout is the v2 value — i.e. the matching v2
    //    preset (prospectus / press / signal) under VITE_REPORT_THEMES_V2. With
    //    the flag OFF nothing resolves to these values, so they are byte-inert. ──
    case 'prospectus':     return <ProspectusCover {...coverProps} />;
    case 'frontispiece':   return <FrontispieceCover {...coverProps} />;
    case 'title-block':    return <TitleBlockCover {...coverProps} />;
    // ── Golden convergence (GC-W2) — the investment-memo title page; default
    //    cover of the `memo` preset / the stock default template. ──
    case 'memo':           return <MemoCover {...coverProps} />;
    case 'classic':
    default:               return <ClassicCover {...coverProps} />;
  }
}
